// BEAM UI primitive — DataTable (W5). A dense, multi-column table for wide (web) viewports. Columns
// declare a header, a flex weight, alignment, and a per-row renderer. Intended for desktop list/report
// density; screens fall back to their stacked card/row layout on narrow viewports (see useResponsive).
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '../../theme';
import { Text } from './Text';

export type Column<T> = {
  key: string;
  header: string;
  /** Flex weight for the column (default 1). */
  flex?: number;
  align?: 'left' | 'right';
  render: (row: T) => React.ReactNode;
};

export function DataTable<T>({
  columns,
  rows,
  keyExtractor,
}: {
  columns: Column<T>[];
  rows: T[];
  keyExtractor: (row: T) => string;
}) {
  return (
    <View>
      <View style={[styles.row, styles.headerRow]}>
        {columns.map((c) => (
          <View key={c.key} style={[styles.cell, { flex: c.flex ?? 1 }, c.align === 'right' && styles.right]}>
            <Text variant="label" color="secondary" numberOfLines={1}>{c.header}</Text>
          </View>
        ))}
      </View>
      {rows.map((r) => (
        <View key={keyExtractor(r)} style={styles.row}>
          {columns.map((c) => (
            <View key={c.key} style={[styles.cell, { flex: c.flex ?? 1 }, c.align === 'right' && styles.right]}>
              {c.render(r)}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
  },
  headerRow: { borderTopWidth: 0 },
  cell: { flex: 1, justifyContent: 'center' },
  right: { alignItems: 'flex-end' },
});
